import {
    afterNextRender,
    DestroyRef,
    Inject,
    Injectable,
    Injector,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, EventType, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { PrimeNG } from 'primeng/config';
import {
    BehaviorSubject,
    filter,
    map,
    Observable,
    switchMap,
    take,
    tap,
} from 'rxjs';
import { LanguageCode } from '../models/language-code';
import { langCodeToIntlFEData } from '../models/languages-fe-model';
import { localStorageToken } from '../tokens/localStorage.token';
import { LocalizationService } from './localization.service';

@Injectable({
    providedIn: 'root',
})
export class LocaleService {
    readonly DEFAULT_LANG = this.localizationService.defaultLanguage;

    private readonly LOCALE_KEY = 'rem-locale';
    private readonly URL_PARAM_KEY = 'lang';

    private readonly languageSelected: BehaviorSubject<boolean> =
        new BehaviorSubject(false);
    /**
     * Emits `true` once the resolved language's translations, including PrimeNG's, have been
     * applied. Consumers that render translated PrimeNG chrome (e.g. `ErrorService` toasts)
     * should gate on this to avoid rendering with PrimeNG's untranslated default and then
     * flipping the text after Angular has already checked it (NG0100).
     */
    public languageSelected$: Observable<boolean> =
        this.languageSelected.asObservable();

    constructor(
        private readonly destroyRef: DestroyRef,
        private readonly injector: Injector,
        private readonly localizationService: LocalizationService,
        @Inject(localStorageToken) private readonly localStorage: Storage,
        private readonly messageService: MessageService,
        private readonly primeNG: PrimeNG,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly translateService: TranslateService
    ) {}

    /**
     * Handles application languages including setting up default language, registering languages,
     * retrieving locale from URL, watching for language changes, and setting PrimeNG locales.
     */
    public handleApplicationLanguages() {
        // Default language will be used as a fallback when a translation isn't found for the current language
        const defaultLanguageKey: LanguageCode = this.DEFAULT_LANG;
        this.translateService.setFallbackLang(defaultLanguageKey);

        // Get all languages defined for FE and register them within ngx-translate
        const langCodes = [...langCodeToIntlFEData.keys()];
        this.translateService.addLangs(langCodes);

        // To be able to retrieve locale from URL, we need to wait until navigation ends
        this.router.events
            .pipe(
                filter((result) => result.type === EventType.NavigationEnd),
                take(1), // check the URL only once after full page reload
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (_result) => {
                    const locale = this.getClientLocale();
                    this.translateService
                        .use(
                            langCodes.includes(locale as LanguageCode)
                                ? locale
                                : this.DEFAULT_LANG
                        )
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe();
                },
            });

        // Watch for language changes - update the value in local storage
        this.monitorLocaleChangeToUpdateLocaleStorage();

        // PrimeNG locales
        this.setPrimeNGLocales();
    }

    /**
     * Retrieves the flag code corresponding to a given language code.
     *
     * @param langCode The language code for which the flag code is to be retrieved.
     * @returns The flag code associated with the provided language code.
     */
    public static getFlagCodeFromLanguageCode(langCode: LanguageCode): string {
        return langCodeToIntlFEData.get(langCode)?.flagCode ?? '';
    }

    /**
     * Retrieves the client's locale using the following priority:
     *
     * @returns The client's locale.
     */
    private getClientLocale(): string {
        // 1. Locale is specified in URL path
        const localeFromUrl =
            this.route.snapshot.queryParams[this.URL_PARAM_KEY];
        if (localeFromUrl) return localeFromUrl;

        // 2. Locale is stored in local storage
        const localeFromLocaleStorage = this.retrieveLocaleFromLocalStorage();
        if (localeFromLocaleStorage) return localeFromLocaleStorage;

        // 3. Get locale from client's browser
        const localeFromClientBrowser = this.translateService.getBrowserLang();
        if (localeFromClientBrowser) return localeFromClientBrowser;

        // 4. Use default language
        return this.localizationService.defaultLanguage;
    }

    /**
     * Monitors locale changes and updates the locale in local storage accordingly.
     */
    private monitorLocaleChangeToUpdateLocaleStorage(): void {
        this.translateService.onLangChange
            .pipe(
                map((result) => result.lang),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (result) => {
                    this.storeLocaleToLocalStorage(result as LanguageCode);
                },
            });
    }

    /**
     * Retrieves the selected locale from local storage.
     *
     * @returns The selected locale, or null if not found.
     */
    private retrieveLocaleFromLocalStorage(): string | null {
        return this.localStorage.getItem(this.LOCALE_KEY);
    }

    /**
     * Stores the selected locale to local storage.
     *
     * @param lang The language code to be stored.
     */
    private storeLocaleToLocalStorage(lang: LanguageCode): void {
        this.localStorage.setItem(this.LOCALE_KEY, lang);
    }

    /**
     * Keeps PrimeNG’s i18n in sync with ngx-translate on language change.
     *
     * On an actual language *switch* it clears toasts first (avoids NG0100), then applies
     * the `primeNG` translation via `PrimeNG.setTranslation`. Toasts get dismissed on every
     * switch - acceptable since that's rare. The initial language resolution is exempt:
     * there is nothing stale to clear yet. `languageSelected$` only flips to `true` once this
     * initial `setTranslation` has actually run, so consumers gating on it (e.g.
     * `ErrorService`) never render PrimeNG chrome - e.g. a toast's close-button aria-label -
     * with PrimeNG's untranslated default and then flip it under Angular's nose, which is
     * what causes an NG0100 on the very first toast (such as one raised by a route guard
     * redirecting on an invalid route param before the app's language has resolved).
     *
     * `setTranslation` is deferred via `afterNextRender` (CSD-1494): PrimeNG only
     * regenerates translated `@Input`s like `p-columnFilter` `matchModeOptions` when its
     * translation observer fires, not on Input changes. Since `onLangChange` is synchronous
     * and this subscription runs before components re-translate those inputs, firing
     * immediately left them one language change behind - deferring past the next render
     * fixes that and holds under zoneless CD too.
     */
    private setPrimeNGLocales(): void {
        let appliedLang: string | undefined;

        this.translateService.onLangChange
            .pipe(
                tap(({ lang }) => {
                    if (appliedLang !== undefined && appliedLang !== lang) {
                        this.messageService.clear();
                    }

                    appliedLang = lang;
                }),
                switchMap(() => this.translateService.get('primeNG')),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((res) => {
                // afterNextRender needs an injection context by default; this runs in a
                // subscribe callback, so the constructor-captured injector is passed explicitly.
                afterNextRender(
                    () => {
                        this.primeNG.setTranslation(res);
                        this.languageSelected.next(true);
                    },
                    { injector: this.injector }
                );
            });
    }
}
